/*
  <hero-slides>: rotation for the homepage hero (sections/hero-slides.liquid, chunk 03).

  The active dot's fill runs a Web Animation as long as the slide interval; when it finishes, the
  next slide shows. Pausing pauses that animation, so a slide resumes with the time it had left.
  Rotation runs only with autoplay on and when the visitor doesn't prefer reduced motion, and it
  waits while:
  - a mouse is over the hero;
  - keyboard focus is inside it;
  - the pause button is pressed (until it's pressed again; this also stops the art's float);
  - the hero is out of view or the tab is hidden;
  - a slide is selected in the theme editor.

  The dots are tabs: Left and Right arrows, Home and End move between them and show that slide.
  The previous and next buttons and a horizontal swipe change slides too. Images of the later
  slides stay undisplayed (so the browser doesn't fetch them) until the page has loaded or the
  visitor changes slides. No dependencies.
*/

if (!customElements.get('hero-slides')) {
  class HeroSlides extends HTMLElement {
    constructor() {
      super();
      this.reveal = this.reveal.bind(this);
      this.syncState = this.syncState.bind(this);
      this.startProgress = this.startProgress.bind(this);
    }

    connectedCallback() {
      if (!this.stage && !this.setUp()) return;

      document.addEventListener('visibilitychange', this.syncState);
      this.reducedMotion.addEventListener('change', this.startProgress);
      if ('IntersectionObserver' in window) {
        this.observer = new IntersectionObserver((entries) => {
          this.hold('offscreen', !entries[entries.length - 1].isIntersecting);
        });
        this.observer.observe(this);
      }

      if (window.Shopify?.designMode || document.readyState === 'complete') {
        this.reveal();
      } else {
        window.addEventListener('load', this.reveal, { once: true });
      }

      this.startProgress();
    }

    disconnectedCallback() {
      if (!this.stage) return;
      document.removeEventListener('visibilitychange', this.syncState);
      this.reducedMotion.removeEventListener('change', this.startProgress);
      window.removeEventListener('load', this.reveal);
      this.observer?.disconnect();
      this.observer = null;
      this.progress?.cancel();
      this.progress = null;
    }

    // Runs once per element: reads the markup and wires the controls. Returns false when there's
    // nothing to rotate.
    setUp() {
      this.slides = Array.from(this.querySelectorAll('[data-hero-slide]'));
      this.dots = Array.from(this.querySelectorAll('[data-hero-dot]'));
      if (this.slides.length < 2) return false;

      this.stage = this.querySelector('.hero-slides__stage');
      this.toggleButton = this.querySelector('[data-hero-toggle]');
      this.index = Math.max(
        0,
        this.slides.findIndex((slide) => slide.classList.contains('is-active'))
      );
      this.duration = (Number(this.dataset.interval) || 6) * 1000;
      this.autoplay = this.hasAttribute('data-autoplay');
      this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.held = { hover: false, focus: false, user: false, offscreen: false, editor: false };

      this.querySelector('[data-hero-prev]')?.addEventListener('click', () => this.go(this.index - 1));
      this.querySelector('[data-hero-next]')?.addEventListener('click', () => this.go(this.index + 1));
      this.toggleButton?.addEventListener('click', () => this.hold('user', !this.held.user));
      this.dots.forEach((dot, i) => dot.addEventListener('click', () => this.go(i)));
      this.querySelector('[data-hero-dots]')?.addEventListener('keydown', (event) => this.onDotKeydown(event));

      this.addEventListener('pointerenter', (event) => {
        if (event.pointerType === 'mouse') this.hold('hover', true);
      });
      this.addEventListener('pointerleave', (event) => {
        if (event.pointerType === 'mouse') this.hold('hover', false);
      });
      // Only keyboard focus holds the rotation; a mouse click on a dot or arrow doesn't.
      this.addEventListener('focusin', (event) => this.hold('focus', this.isKeyboardFocus(event.target)));
      this.addEventListener('focusout', (event) => {
        if (!this.contains(event.relatedTarget)) this.hold('focus', false);
      });

      this.stage.addEventListener('pointerdown', (event) => this.onPointerDown(event));
      this.stage.addEventListener('pointerup', (event) => this.onPointerUp(event));
      this.stage.addEventListener('pointercancel', () => {
        this.swipe = null;
      });

      this.addEventListener('shopify:block:select', (event) => {
        const index = this.slides.indexOf(event.target);
        if (index < 0) return;
        this.held.editor = true;
        this.go(index);
      });
      this.addEventListener('shopify:block:deselect', () => this.hold('editor', false));
      return true;
    }

    go(target) {
      const count = this.slides.length;
      const next = ((target % count) + count) % count;
      this.reveal();

      if (next !== this.index) {
        this.slides[this.index].classList.remove('is-active');
        this.slides[next].classList.add('is-active');
        this.dots.forEach((dot, i) => {
          dot.setAttribute('aria-selected', String(i === next));
          dot.tabIndex = i === next ? 0 : -1;
        });
        this.index = next;
      }

      this.startProgress();
    }

    // Restarts the active dot's fill; the slide changes when it finishes.
    startProgress() {
      this.progress?.cancel();
      this.progress = null;

      const fill = this.dots[this.index]?.querySelector('.hero-slides__dot-fill');
      if (this.autoplay && !this.reducedMotion.matches && fill && typeof fill.animate === 'function') {
        const animation = fill.animate([{ transform: 'scaleX(0)' }, { transform: 'scaleX(1)' }], {
          duration: this.duration,
          easing: 'linear',
          fill: 'forwards',
        });
        animation.onfinish = () => {
          if (this.progress === animation) this.go(this.index + 1);
        };
        this.progress = animation;
      }

      this.syncState();
    }

    hold(reason, value) {
      if (this.held[reason] === value) return;
      this.held[reason] = value;
      this.syncState();
    }

    syncState() {
      const waiting = document.hidden || Object.values(this.held).some(Boolean);

      if (this.progress) {
        if (waiting && this.progress.playState === 'running') this.progress.pause();
        if (!waiting && this.progress.playState === 'paused') this.progress.play();
      }

      // Announce slide changes only while the hero isn't rotating on its own.
      this.stage.setAttribute('aria-live', this.progress && !waiting ? 'off' : 'polite');
      this.toggleAttribute('data-still', this.held.user || this.held.offscreen);
      this.toggleAttribute('data-user-paused', this.held.user);
      if (this.toggleButton) {
        this.toggleButton.setAttribute(
          'aria-label',
          this.held.user ? this.toggleButton.dataset.labelPlay : this.toggleButton.dataset.labelPause
        );
      }
    }

    reveal() {
      this.setAttribute('data-revealed', '');
    }

    onDotKeydown(event) {
      const current = this.dots.indexOf(event.target.closest('[data-hero-dot]'));
      if (current < 0) return;

      const last = this.dots.length - 1;
      let target;
      switch (event.key) {
        case 'ArrowRight':
          target = current === last ? 0 : current + 1;
          break;
        case 'ArrowLeft':
          target = current === 0 ? last : current - 1;
          break;
        case 'Home':
          target = 0;
          break;
        case 'End':
          target = last;
          break;
        default:
          return;
      }

      event.preventDefault();
      this.go(target);
      this.dots[target].focus();
    }

    onPointerDown(event) {
      if (event.pointerType === 'mouse' || !event.isPrimary) return;
      this.swipe = { x: event.clientX, y: event.clientY };
    }

    onPointerUp(event) {
      if (!this.swipe) return;
      const dx = event.clientX - this.swipe.x;
      const dy = event.clientY - this.swipe.y;
      this.swipe = null;
      if (Math.abs(dx) > 40 && Math.abs(dx) > Math.abs(dy) * 1.5) this.go(this.index + (dx < 0 ? 1 : -1));
    }

    isKeyboardFocus(element) {
      try {
        return element.matches(':focus-visible');
      } catch (error) {
        return true;
      }
    }
  }

  customElements.define('hero-slides', HeroSlides);
}
